import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { TimeEntry, TimeEntryStatus } from '../models/TimeEntry';
import { timeEntryProjectId } from '../models/TimeEntry';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../models/User';

export interface UseTimeEntriesOptions {
  /** When set, only entries for this Firebase Auth uid. Omit for all users in scope. */
  viewerUserId?: string | null;
  contractId?: string | null;
  /** Parent client account id (tenant). */
  clientId?: string | null;
  /**
   * @deprecated Use clientId. Kept as alias for parent client scope.
   */
  organizationId?: string | null;
  /** Project document id filter. Use `'none'` for client-level-only (no project). */
  projectId?: string | null;
  type?: string | null;
  dateRange?: {
    start: Date;
    end: Date;
  };
  billable?: boolean;
  status?: TimeEntryStatus;
}

function allowedProjectIdsForProjectAdmin(
  projectAccess?: Record<string, unknown> | null,
  clientAccess?: Record<string, unknown> | null
): Set<string> {
  return new Set([
    ...Object.keys(projectAccess || {}),
    ...Object.keys(clientAccess || {}),
  ]);
}

export function useTimeEntries(options: UseTimeEntriesOptions = {}) {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  const projectAccessKey = useMemo(
    () =>
      [
        ...Object.keys(currentUser?.projectAccess || {}),
        ...Object.keys(currentUser?.clientAccess || {}),
      ]
        .sort()
        .join(','),
    [currentUser?.projectAccess, currentUser?.clientAccess]
  );

  const timeEntryDateRangeStart = options.dateRange?.start?.getTime();
  const timeEntryDateRangeEnd = options.dateRange?.end?.getTime();

  const tenantClientId =
    options.clientId ||
    options.organizationId ||
    currentUser?.clientId ||
    currentUser?.organizationId ||
    null;

  useEffect(() => {
    setLoading(true);

    const constraints: any[] = [];
    const role = currentUser?.role;

    if (options.viewerUserId) {
      constraints.push(where('userId', '==', options.viewerUserId));
    }

    if (options.contractId) {
      constraints.push(where('contractId', '==', options.contractId));
    }

    // Prefer project-scoped query when filtering by project alone or with tenant.
    if (options.projectId && options.projectId !== 'none') {
      constraints.push(where('projectId', '==', options.projectId));
    } else if (options.projectId === 'none') {
      // Client-level only — handled post-query for compatibility with legacy docs.
    } else if (tenantClientId && role !== UserRole.SUPER_ADMIN) {
      constraints.push(where('clientId', '==', tenantClientId));
    } else if (tenantClientId && (options.clientId || options.organizationId)) {
      constraints.push(where('clientId', '==', tenantClientId));
    }

    if (options.type) {
      constraints.push(where('type', '==', options.type));
    }

    if (options.billable !== undefined) {
      constraints.push(where('billable', '==', options.billable));
    }

    if (options.status) {
      constraints.push(where('status', '==', options.status));
    }

    const hasDateRange =
      timeEntryDateRangeStart !== undefined && timeEntryDateRangeEnd !== undefined;
    if (hasDateRange) {
      constraints.push(where('startTime', '>=', Timestamp.fromMillis(timeEntryDateRangeStart)));
      constraints.push(where('startTime', '<=', Timestamp.fromMillis(timeEntryDateRangeEnd)));
    }

    const hasWhereClauses = constraints.length > 0;
    if (hasDateRange) {
      constraints.push(orderBy('startTime', 'desc'));
    } else if (!hasWhereClauses) {
      constraints.push(orderBy('startTime', 'desc'));
    }

    let q;
    let needsSorting = false;
    if (constraints.length > 0) {
      q = query(collection(db, 'timeEntries'), ...constraints);
      needsSorting = hasWhereClauses && !hasDateRange;
    } else if (role === UserRole.SUPER_ADMIN) {
      q = query(collection(db, 'timeEntries'), orderBy('startTime', 'desc'));
    } else if (tenantClientId) {
      q = query(collection(db, 'timeEntries'), where('clientId', '==', tenantClientId));
      needsSorting = true;
    } else {
      setTimeEntries([]);
      setLoading(false);
      return;
    }

    const allowedIds = allowedProjectIdsForProjectAdmin(
      currentUser?.projectAccess,
      currentUser?.clientAccess
    );

    const applyProjectAdminFilter = (entries: TimeEntry[]) => {
      if (role !== UserRole.PROJECT_ADMIN) return entries;
      return entries.filter((e) => {
        const pid = timeEntryProjectId(e);
        return !!pid && allowedIds.has(pid);
      });
    };

    const applyClientLevelFilter = (entries: TimeEntry[]) => {
      if (options.projectId !== 'none') return entries;
      return entries.filter((e) => !timeEntryProjectId(e));
    };

    const applyTenantFilterFallback = (entries: TimeEntry[]) => {
      // When querying by projectId alone, still optionally narrow by tenant if provided.
      if (!options.projectId || options.projectId === 'none') return entries;
      if (!options.clientId && !options.organizationId) return entries;
      const tenant = options.clientId || options.organizationId;
      return entries.filter(
        (e) => e.clientId === tenant || e.organizationId === tenant
      );
    };

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let entriesData = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            clientId: data.clientId || data.organizationId || '',
            organizationId: data.organizationId || data.clientId || '',
            projectId: data.projectId || undefined,
            startTime: data.startTime?.toDate() || new Date(),
            endTime: data.endTime?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as TimeEntry;
        });

        if (needsSorting) {
          entriesData.sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
        }

        entriesData = applyTenantFilterFallback(entriesData);
        entriesData = applyClientLevelFilter(entriesData);
        entriesData = applyProjectAdminFilter(entriesData);

        setTimeEntries(entriesData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching time entries:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [
    options.viewerUserId,
    options.contractId,
    options.organizationId,
    options.clientId,
    options.projectId,
    options.type,
    options.billable,
    options.status,
    timeEntryDateRangeStart,
    timeEntryDateRangeEnd,
    currentUser?.role,
    currentUser?.organizationId,
    currentUser?.clientId,
    currentUser?.clientAccess,
    currentUser?.projectAccess,
    projectAccessKey,
    tenantClientId,
  ]);

  const createTimeEntry = async (entryData: Omit<TimeEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const clientId = entryData.clientId || entryData.organizationId || '';
      const docRef = await addDoc(collection(db, 'timeEntries'), {
        ...entryData,
        clientId,
        organizationId: entryData.organizationId || clientId,
        startTime: Timestamp.fromDate(entryData.startTime),
        endTime: Timestamp.fromDate(entryData.endTime),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating time entry:', err);
      throw err;
    }
  };

  const updateTimeEntry = async (entryId: string, updates: Partial<TimeEntry>) => {
    try {
      const entryRef = doc(db, 'timeEntries', entryId);
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now(),
      };

      if (updates.clientId && !updates.organizationId) {
        updateData.organizationId = updates.clientId;
      }
      if (updates.organizationId && !updates.clientId) {
        updateData.clientId = updates.organizationId;
      }

      if (updates.startTime) {
        updateData.startTime = Timestamp.fromDate(updates.startTime);
      }
      if (updates.endTime) {
        updateData.endTime = Timestamp.fromDate(updates.endTime);
      }

      await updateDoc(entryRef, updateData);
    } catch (err) {
      console.error('Error updating time entry:', err);
      throw err;
    }
  };

  const deleteTimeEntry = async (entryId: string) => {
    try {
      const entryRef = doc(db, 'timeEntries', entryId);
      await deleteDoc(entryRef);
    } catch (err) {
      console.error('Error deleting time entry:', err);
      throw err;
    }
  };

  const rejectTimeEntry = async (entryId: string) => {
    return updateTimeEntry(entryId, { status: 'rejected' as TimeEntryStatus });
  };

  const markAsBilled = async (entryId: string) => {
    return updateTimeEntry(entryId, { status: 'billed' as TimeEntryStatus });
  };

  const totalDuration = timeEntries.reduce((sum, entry) => sum + entry.duration, 0);
  const billableDuration = timeEntries
    .filter((entry) => entry.billable)
    .reduce((sum, entry) => sum + entry.duration, 0);

  return {
    timeEntries,
    loading,
    error,
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    rejectTimeEntry,
    markAsBilled,
    totalDuration,
    billableDuration,
  };
}
